import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { ERROR_CODES, type PanelCreateOrganizationInput } from '@icebreaker/shared';
import { Button, Field, Input, Modal, Select, urlInputProps, useFormErrors } from '@icebreaker/ui';
import { organizationDetailPath } from '@/app/routes';
import { subSurfacePermission } from '@/app/screen-permissions';
import { PermissionSurfaceGate } from '@/features/access/ui/permission-gate';
import { isLinkableLead } from '@/features/sales/model/sales-query';
import { numericDateOptions, numericDateTimeOptions } from '@/features/sales/model/sales-view';
import { fetchSalesInquiryPicker } from '@/features/sales/store/sales-inquiries.slice';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { provisionOrganization, provisionReset } from '../store/organizations.slice';
import {
  DialogFooter,
  DialogSub,
  InfoBlock,
  ServerBanner,
  UnknownOutcome,
  failureKind,
  fieldErrorPaths,
  looksLikeEmail,
  useReleaseKeyOnSettle,
  useStaffName,
  useWriteKey,
} from './dialog-parts';
import {
  OwnerEmailConfirm,
  OwnerEmailField,
  normalizeOwnerEmail,
  useOwnerEmailGate,
} from './owner-email-check';
import styles from './organizations.module.css';

export interface CreateOrganizationPrefill {
  readonly name: string;
  readonly ownerEmail: string;
  readonly lead: { readonly id: string; readonly name: string; readonly createdAt: string } | null;
}

/** Kurum detayına "Kredi yükle" diyaloğu açık gidilir (P4 başarı hâli). */
export const GRANT_ON_OPEN_PARAM = 'krediYukle';

/**
 * KURUM AÇ VE SAHİBE DAVET (P4).
 *
 * Kurum doğrulanmış doğar, sahibe e-posta daveti kuyruğa düşer ve 1 deneme
 * kredisi yazılır (sunucu tek transaction'da yapar). İsim çakışması kapısı
 * personel elinde de koşar (V14): hata "Kurum adı" alanının altına yazılır.
 *
 * Çift basış: gönderim sürerken alanlar ve düğme kapalıdır. Sunucu aynı adı
 * ikinci kez açmaz (doğrulanmış ada yakın ad reddedilir), yani hızlı iki basış
 * en fazla bir kurum yazar.
 *
 * Sahip adresi bir kişisel hesaptaysa alan uyarır ve gönderim ikinci onay
 * ister (KH8 · Story H7 · TP1, `owner-email-check.tsx`).
 */
export function CreateOrganizationDialog({
  prefill,
  onClose,
}: {
  prefill?: CreateOrganizationPrefill;
  onClose: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const staffName = useStaffName();
  const provision = useAppSelector((root: RootState) => root.panelOrgs.provision);
  const picker = useAppSelector((root: RootState) => root.panelSales.picker);
  const [name, setName] = useState(prefill?.name ?? '');
  const [ownerEmail, setOwnerEmail] = useState(prefill?.ownerEmail ?? '');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [leadId, setLeadId] = useState(prefill?.lead?.id ?? '');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    dispatch(provisionReset());
    return () => {
      dispatch(provisionReset());
    };
  }, [dispatch]);

  const busy = provision.phase === 'loading';
  const result = provision.result;
  // Denetim satırının anı: başarı yanıtının ekrana düştüğü an (sunucu kaydıyla aynı dakika).
  const completedAt = useMemo(() => (result ? new Date(Date.now()).toISOString() : null), [result]);
  const serverPaths = fieldErrorPaths(provision.error);
  const nameClash = provision.error?.code === ERROR_CODES.ORGANIZATION_NAME_CONFLICT;
  // Korunan ad yalnız panel yolunda gövdede gelir (sunucu, personel aktörüyle).
  const protectedName = nameClash ? (provision.error?.nameConflict?.protectedName ?? null) : null;
  const outcome = failureKind(provision.phase, provision.error);
  const serverFailed = outcome !== 'none' && !nameClash;
  // Kurum açmanın yineleme anahtarı (T4 QA P2-1): kopmadan sonra kapat-aç-yeniden
  // gönder aynı anahtarla gider, sunucu ikinci kurumu açmaz, ilkini döndürür.
  const writeKey = useWriteKey('provision');
  useReleaseKeyOnSettle(provision.phase, outcome, writeKey.release);

  // Sunucunun alan cümleleri (bilinen alanlar) tasarım cümlesi yoksa ALANIN ALTINA düşer.
  const serverForm = useFormErrors(provision.error, ['name', 'contactEmail', 'websiteUrl']);
  const errors = touched ? validate() : {};
  const nameError =
    errors.name ??
    (nameClash
      ? protectedName
        ? t.t('admin.orgs.createDialog.nameClashNamed', { name: protectedName })
        : t.t('admin.orgs.createDialog.nameClash')
      : serverPaths.has('name')
        ? (serverForm.at('name') ?? t.t('admin.orgs.createDialog.nameShort'))
        : undefined);
  const ownerError =
    errors.ownerEmail ??
    (serverPaths.has('ownerEmail') ? t.t('admin.orgs.createDialog.emailInvalid') : undefined);
  const contactError =
    errors.contactEmail ??
    (serverPaths.has('contactEmail')
      ? (serverForm.at('contactEmail') ?? t.t('admin.orgs.createDialog.emailInvalid'))
      : undefined);
  const websiteError =
    errors.websiteUrl ??
    (serverPaths.has('websiteUrl')
      ? (serverForm.at('websiteUrl') ?? t.t('admin.orgs.createDialog.websiteInvalid'))
      : undefined);

  function validate(): Partial<
    Record<'name' | 'ownerEmail' | 'contactEmail' | 'websiteUrl', string>
  > {
    const out: Partial<Record<'name' | 'ownerEmail' | 'contactEmail' | 'websiteUrl', string>> = {};
    const trimmed = name.trim();
    if (trimmed.length === 0) out.name = t.t('admin.orgs.createDialog.nameRequired');
    else if (trimmed.length < 2) out.name = t.t('admin.orgs.createDialog.nameShort');
    if (ownerEmail.trim().length === 0)
      out.ownerEmail = t.t('admin.orgs.createDialog.emailRequired');
    else if (!looksLikeEmail(ownerEmail))
      out.ownerEmail = t.t('admin.orgs.createDialog.emailInvalid');
    if (contactEmail.trim() && !looksLikeEmail(contactEmail)) {
      out.contactEmail = t.t('admin.orgs.createDialog.emailInvalid');
    }
    if (websiteUrl.trim() && (/\s/.test(websiteUrl.trim()) || !websiteUrl.includes('.'))) {
      out.websiteUrl = t.t('admin.orgs.createDialog.websiteInvalid');
    }
    return out;
  }

  const send = () => {
    const body: Omit<PanelCreateOrganizationInput, 'idempotencyKey'> = {
      name: name.trim(),
      ownerEmail: ownerEmail.trim(),
      websiteUrl: websiteUrl.trim() || null,
      contactEmail: contactEmail.trim() || null,
      salesInquiryId: leadId || null,
    };
    const signature = JSON.stringify({ ...body, ownerEmail: body.ownerEmail.toLowerCase() });
    dispatch(
      provisionOrganization.request({ ...body, idempotencyKey: writeKey.keyFor(signature) }),
    );
  };
  const gate = useOwnerEmailGate({ email: ownerEmail, send, inputId: 'org-owner' });
  const { check: checkOwner, status: ownerStatus } = gate;
  // Satış adayından gelen adres odaktan hiç çıkmayabilir: alan o adresi
  // taşıdığı sürece sonucu yoksa sorulur (yeniden bağlanan efektlerde de).
  const prefillOwner = normalizeOwnerEmail(prefill?.ownerEmail ?? '');
  const ownerIsPrefill = prefillOwner !== '' && normalizeOwnerEmail(ownerEmail) === prefillOwner;
  useEffect(() => {
    if (ownerIsPrefill && ownerStatus === 'idle') checkOwner();
  }, [checkOwner, ownerIsPrefill, ownerStatus]);

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (busy) return;
    setTouched(true);
    if (Object.keys(validate()).length > 0) return;
    gate.request();
  };

  if (result) {
    const goDetail = (grant: boolean) => {
      onClose();
      void navigate(
        `${organizationDetailPath(result.organizationId)}${grant ? `?${GRANT_ON_OPEN_PARAM}=1` : ''}`,
      );
    };
    return (
      <Modal
        open
        title={t.t('admin.orgs.createDialog.successTitle', { name: result.name })}
        closeLabel={t.t('common.close')}
        onClose={onClose}
        footer={
          <DialogFooter
            audit={t.t('admin.orgs.createDialog.successAudit', {
              name: staffName,
              date: t.formatDate(completedAt ?? '', numericDateTimeOptions(t.locale)),
            })}
          >
            <Button variant="primary" onClick={() => goDetail(true)}>
              {t.t('admin.orgs.createDialog.goGrant')}
            </Button>
            <Button variant="primary" onClick={() => goDetail(false)}>
              {t.t('admin.orgs.createDialog.goDetail')}
            </Button>
          </DialogFooter>
        }
      >
        <DialogSub>
          <span className={styles.mono}>{result.ownerInvitation.email}</span>
        </DialogSub>
        <ul className={styles.success} role="status">
          {[
            t.t('admin.orgs.createDialog.successVerified'),
            t.t('admin.orgs.createDialog.successInvited'),
            t.t('admin.orgs.createDialog.successTrial'),
          ].map((line) => (
            <li key={line} className={styles.successLine}>
              <Check size={14} aria-hidden="true" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  if (gate.confirming) {
    return (
      <OwnerEmailConfirm
        email={ownerEmail.trim()}
        staffName={staffName}
        onSendAnyway={gate.sendAnyway}
        onChange={gate.changeAddress}
      />
    );
  }

  const leadOptions = picker.items.filter(
    (row) => isLinkableLead(row) && row.id !== prefill?.lead?.id,
  );

  return (
    <Modal
      open
      title={t.t('admin.orgs.createDialog.title')}
      closeLabel={t.t('common.close')}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <DialogFooter audit={t.t('admin.orgs.auditLine', { name: staffName })}>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t.t('admin.orgs.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="create-organization-form"
            loading={busy}
            loadingLabel={t.t('admin.orgs.createDialog.submitting')}
          >
            {serverFailed ? t.t('admin.orgs.retry') : t.t('admin.orgs.createDialog.submit')}
          </Button>
        </DialogFooter>
      }
    >
      {prefill?.lead ? (
        <DialogSub>
          {t.t('admin.orgs.createDialog.fromLead', { name: prefill.lead.name })}
        </DialogSub>
      ) : null}
      <form id="create-organization-form" className={styles.form} onSubmit={submit} noValidate>
        <Field id="org-name" label={t.t('admin.orgs.createDialog.name')} error={nameError}>
          <Input
            id="org-name"
            value={name}
            disabled={busy}
            invalid={Boolean(nameError)}
            placeholder={t.t('admin.orgs.createDialog.namePlaceholder')}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <OwnerEmailField
          id="org-owner"
          value={ownerEmail}
          status={gate.status}
          error={ownerError}
          disabled={busy}
          placeholder={t.t('admin.orgs.createDialog.ownerEmailPlaceholder')}
          onChange={setOwnerEmail}
          onBlur={gate.check}
          onRetry={gate.check}
        />
        <Field
          id="org-website"
          label={<OptionalLabel text={t.t('admin.orgs.createDialog.website')} />}
          error={websiteError}
        >
          <Input
            id="org-website"
            {...urlInputProps}
            value={websiteUrl}
            disabled={busy}
            invalid={Boolean(websiteError)}
            placeholder="https://"
            onChange={(event) => setWebsiteUrl(event.target.value)}
          />
        </Field>
        <Field
          id="org-contact"
          label={<OptionalLabel text={t.t('admin.orgs.createDialog.contactEmail')} />}
          error={contactError}
        >
          <Input
            id="org-contact"
            type="email"
            className={styles.mono}
            autoComplete="off"
            value={contactEmail}
            disabled={busy}
            invalid={Boolean(contactError)}
            placeholder={t.t('admin.orgs.createDialog.contactEmailPlaceholder')}
            onChange={(event) => setContactEmail(event.target.value)}
          />
        </Field>
        <PermissionSurfaceGate
          permission={subSurfacePermission('panel:create-organization-lead-picker')}
          fallback={null}
        >
          <LeadPicker
            value={leadId}
            disabled={busy}
            onChange={setLeadId}
            prefillLead={prefill?.lead ?? null}
            options={leadOptions.map((row) => ({
              id: row.id,
              label: t.t('admin.orgs.createDialog.leadOption', {
                name: row.companyName ?? '',
                date: t.formatDate(row.createdAt, numericDateOptions(t.locale)),
              }),
            }))}
          />
        </PermissionSurfaceGate>
        <InfoBlock tone="info">{t.t('admin.orgs.createDialog.info')}</InfoBlock>
        {outcome === 'unknown' ? (
          <UnknownOutcome reloaded={t.t('admin.write.reloadedOrganizations')} />
        ) : serverFailed ? (
          <ServerBanner>{t.t('admin.orgs.createDialog.serverError')}</ServerBanner>
        ) : null}
      </form>
    </Modal>
  );
}

function OptionalLabel({ text }: { text: string }) {
  const t = useTranslation();
  return (
    <>
      {text} <span className={styles.optional}>{t.t('admin.orgs.optional')}</span>
    </>
  );
}

function LeadPicker({
  value,
  disabled,
  onChange,
  options,
  prefillLead,
}: {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  options: readonly { id: string; label: string }[];
  prefillLead: CreateOrganizationPrefill['lead'];
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const phase = useAppSelector((root: RootState) => root.panelSales.picker.phase);
  const query = useAppSelector((root: RootState) => root.panelSales.picker.query);
  // Seçici yalnız yetkili personelde ÇİZİLİR ve yalnız o zaman doldurulur.
  // Kurum aramasının aday ipucu aynı dalı aramalı doldurmuş olabilir: o
  // durumda seçici aramasız listeyle yeniden doldurulur.
  useEffect(() => {
    if (phase === 'idle' || (phase !== 'loading' && query !== '')) {
      dispatch(fetchSalesInquiryPicker.request({ page: 1, pageSize: 50 }));
    }
  }, [dispatch, phase, query]);
  return (
    <Field id="org-lead" label={<OptionalLabel text={t.t('admin.orgs.createDialog.lead')} />}>
      <Select
        id="org-lead"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{t.t('admin.orgs.createDialog.leadNone')}</option>
        {prefillLead ? (
          <option value={prefillLead.id}>
            {t.t('admin.orgs.createDialog.leadOption', {
              name: prefillLead.name,
              date: t.formatDate(prefillLead.createdAt, numericDateOptions(t.locale)),
            })}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}
